/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { createContext, useContext } from 'react';

import type { RecaptchaContextParams } from './recaptcha.types';

const CONTEXT_ERROR =
    'Recaptcha Context is missing. useRecaptcha must be used within a RecaptchaProvider';

/**
 * Default context value for Recaptcha provider.
 * Methods throw errors when used outside the provider.
 */
const defaultContextValue: RecaptchaContextParams = {
    instance: undefined,
    isRecaptchaReady: false,
    v3SiteKey: process.env.RECAPTCHA_ENTERPRISE_KEY_INVIS ?? '',
    v2SiteKey: process.env.RECAPTCHA_ENTERPRISE_KEY ?? '',
    executeV3: () => {
        throw new Error(CONTEXT_ERROR);
    },
};

/**
 * Context for Recaptcha provider.
 * Provides script loading and v3 execution capability.
 */
export const RecaptchaContext =
    createContext<RecaptchaContextParams>(defaultContextValue);

/**
 * Hook to access the core Recaptcha context (script loading and v3 execution).
 *
 * For most use cases, prefer useRecaptchaWithFallback which provides
 * isolated fallback state management.
 *
 * @returns The Recaptcha context values
 * @throws Error if used outside of RecaptchaProvider
 *
 * @example
 * // Raw v3 execution (no fallback handling)
 * const { executeV3, instance } = useRecaptcha();
 * const token = await executeV3('submitForm');
 */
export function useRecaptcha(): RecaptchaContextParams {
    const context = useContext(RecaptchaContext);

    if (!context) {
        throw new Error(CONTEXT_ERROR);
    }

    return context;
}
